// Field-grid identity for an Rx. Cells come from GET /grid. Covariate columns
// come from GET /rx-inputs, already joined on cell_id into keep order
// (CELL-000001 is index 0). This module never reorders an array to turn
// stack row order into grid order. A legacy document is re-read from the
// server, which does that remap, and then trusted.
import { api } from "../api";
import { ApiError } from "../api/unwrap";
import { grossKFixation, kBuildupEfficiency, type KFixSet } from "./kFixation";
import { hydrateFertRx, type SavedRecSet } from "./fertSets";
import { isFrozen } from "./frozenRx";
import type { FieldSoilComposition } from "../types/contract";

export const BOUNDARY_CHANGED = "Field boundary changed; rebuild the Rx.";
export const STACK_MISMATCH =
  "stack doesn't match the field grid; rebuild the covariates";

export const IDW_CA = "sample__idw_ca";
export const IDW_MG = "sample__idw_mg";
export const DIFF_P = "diffusion__corr_p_effective";
export const DIFF_K = "diffusion__corr_k_effective";
export const MAP_UNIT = "ssurgo__soil_soilmapunit";
export const CLAY = "ssurgo__soil_clay";

export interface RxCells {
  gridId: string;
  cellCount: number;
  /** Wire order from /grid. Keep order is the only order an Rx may use. */
  cellOrder: "grid_keep";
  coreVersion: string;
  geoms: { type: string; coordinates: unknown }[];
  centroids: { lng: number; lat: number }[];
}

export interface RxInputGap {
  key: string;
  reason: "needs_covariate_build";
}

export interface RxJoin {
  gridId: string;
  columns: Record<string, unknown[]>;
  missing: RxInputGap[];
}

export interface DisabledCovariateToggle {
  input: string;
  message: string;
  basis?: "machine" | "simulated";
  crop?: string;
}

export function covariateBuildMessage(input: string): string {
  return `Needs the covariate build: ${input}.`;
}

export function covariateToggle(
  input: string,
  present: boolean,
  extra: Pick<DisabledCovariateToggle, "basis" | "crop"> = {},
): DisabledCovariateToggle | null {
  if (present) return null;
  return { input, message: covariateBuildMessage(input), ...extra };
}

export function isGridDocument(set: {
  gridId?: unknown;
  cellOrder?: unknown;
}): boolean {
  return (
    typeof set.gridId === "string" &&
    set.gridId.length > 0 &&
    set.cellOrder === "grid"
  );
}

export function gridStamp(
  gridId: string,
  cellCount: number,
): { gridId: string; cellOrder: "grid"; cellCount: number } {
  return { gridId, cellOrder: "grid", cellCount };
}

function toNum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** A column is usable only when /rx-inputs returned it for this grid, at this length. */
export function gridValues(
  join: RxJoin,
  key: string,
  cellCount: number,
): unknown[] | null {
  if (join.missing.some((gap) => gap.key === key)) return null;
  const raw = join.columns[key];
  if (!Array.isArray(raw) || raw.length !== cellCount) return null;
  return raw;
}

export function gridNumbers(
  join: RxJoin,
  key: string,
  cellCount: number,
): (number | null)[] | null {
  const raw = gridValues(join, key, cellCount);
  return raw ? raw.map(toNum) : null;
}

export async function loadRxCells(fieldId: string): Promise<RxCells> {
  const grid = await api.fetchFieldGrid(fieldId);
  if (grid.cell_order !== "grid_keep") {
    throw new Error(BOUNDARY_CHANGED);
  }
  const geoms = grid.geometries ?? [];
  const centroids = grid.centroids ?? [];
  if (
    grid.cell_count !== centroids.length ||
    (geoms.length > 0 && geoms.length !== grid.cell_count)
  ) {
    throw new Error(BOUNDARY_CHANGED);
  }
  return {
    gridId: grid.boundary_version_id,
    cellCount: grid.cell_count,
    cellOrder: "grid_keep",
    coreVersion: grid.core_version,
    geoms,
    centroids,
  };
}

export async function loadRxInputs(
  fieldId: string,
  keys: string[],
): Promise<RxJoin> {
  const unique = [...new Set(keys.filter((key) => key.length > 0))];
  if (!unique.length) return { gridId: "", columns: {}, missing: [] };
  try {
    const joined = await api.fetchRxInputs(fieldId, unique);
    return {
      gridId: joined.grid_id,
      columns: joined.columns ?? {},
      missing: joined.missing ?? [],
    };
  } catch (e) {
    // A merged, hex, or mismatched stack is a 422 from /rx-inputs. Like server AutoRx
    // (absent_join), every input is missing and the Rx still builds.
    if (
      e instanceof ApiError &&
      e.code === "ERR_VALIDATION" &&
      e.message.includes(STACK_MISMATCH)
    ) {
      return {
        gridId: "",
        columns: {},
        missing: unique.map((key) => ({
          key,
          reason: "needs_covariate_build" as const,
        })),
      };
    }
    throw e;
  }
}

/**
 * Columns from a different grid are not an alignment. Drop them and report
 * every requested key missing so the caller disables the toggle.
 */
export function joinForGrid(
  cells: RxCells,
  join: RxJoin,
  keys: string[],
): RxJoin {
  if (!join.gridId || join.gridId === cells.gridId) return join;
  return {
    gridId: join.gridId,
    columns: {},
    missing: keys.map((key) => ({ key, reason: "needs_covariate_build" })),
  };
}

export function kFixEfficiencyOnGrid(
  artifact: FieldSoilComposition,
  mukeys: readonly unknown[],
  clay: readonly unknown[] | null,
  set: KFixSet = "standard",
  release = 0.5,
): (number | null)[] {
  const units = artifact.map_units ?? {};
  return mukeys.map((mk, i) => {
    const mu = units[mk == null ? "" : String(mk)];
    if (!mu) return null;
    const clayV = clay && i < clay.length ? toNum(clay[i]) : null;
    return kBuildupEfficiency(
      grossKFixation(mu.clay_groups ?? {}, clayV, set),
      release,
    );
  });
}

export async function readyRecSet(
  fieldId: string,
  set: SavedRecSet,
): Promise<SavedRecSet> {
  if (set.gridStatus === "rebuild") {
    throw new Error(set.rebuildMessage || BOUNDARY_CHANGED);
  }
  const document = isGridDocument(set)
    ? set
    : await upgradedFromServer(fieldId, set);
  if (document.gridStatus === "rebuild") {
    throw new Error(document.rebuildMessage || BOUNDARY_CHANGED);
  }
  if (!isGridDocument(document)) throw new Error(BOUNDARY_CHANGED);
  return document;
}

async function upgradedFromServer(
  fieldId: string,
  set: SavedRecSet,
): Promise<SavedRecSet> {
  // The browser does not permute soil arrays. GET by id does, in memory.
  // The summary list does not: it has no soil arrays to align.
  try {
    const fresh = (await api.fetchFertRxById(
      fieldId,
      set.id,
    )) as SavedRecSet & {
      cellGeometries?: unknown;
    };
    if (fresh && typeof fresh.id === "string") {
      const { cellGeometries: _cells, ...rest } = fresh;
      return rest;
    }
  } catch (e) {
    if (e instanceof ApiError && e.code === "ERR_DEPENDENCY_UNAVAILABLE") {
      throw e;
    }
    if (
      e instanceof ApiError &&
      e.status === 404 &&
      !isFrozen(set) &&
      set.soil != null
    ) {
      const listed = await hydrateFertRx(fieldId);
      return listed.find((row) => row.id === set.id) ?? set;
    }
    throw e;
  }
  return set;
}

export async function assertCurrentGrid(
  fieldId: string,
  set: SavedRecSet,
): Promise<RxCells> {
  const cells = await loadRxCells(fieldId);
  if (
    set.gridId !== cells.gridId ||
    set.cellOrder !== "grid" ||
    set.cellCount !== cells.cellCount ||
    cells.geoms.length !== cells.cellCount
  ) {
    throw new Error(BOUNDARY_CHANGED);
  }
  return cells;
}
